/**
 * Componente de cronômetro flutuante para tickets
 * Exibe o tempo restante para resposta em tempo real
 */
import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Collapse,
  LinearProgress
} from '@material-ui/core';
import StandardBadge from '../StandardBadge';
import { makeStyles } from '@material-ui/core/styles';
import {
  Timer as TimerIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Minimize as MinimizeIcon,
  Maximize as MaximizeIcon
} from '@material-ui/icons';
import { useTimeMonitoring } from '../../hooks/useTimeMonitoring';
import { i18n } from '../../translate/i18n';

const useStyles = makeStyles((theme) => ({
  timerCard: {
    position: 'fixed',
    top: 20,
    right: 20,
    width: 280,
    maxWidth: '90vw',
    zIndex: 1300,
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(0, 0, 0, 0.1)',
    borderRadius: 12,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s ease-in-out',
    '&.warning': {
      borderColor: theme.palette.warning.main,
      backgroundColor: 'rgba(255, 193, 7, 0.1)',
    },
    '&.timeout': {
      borderColor: theme.palette.error.main,
      backgroundColor: 'rgba(244, 67, 54, 0.1)',
    },
    '&.minimized': {
      width: 120,
      height: 60,
    }
  },
  timerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 16px',
    borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
  },
  timerContent: {
    padding: '16px !important',
    textAlign: 'center',
  },
  timerDisplay: {
    fontSize: '2rem',
    fontWeight: 'bold',
    fontFamily: 'monospace',
    margin: '8px 0',
    '&.warning': {
      color: theme.palette.warning.main,
    },
    '&.timeout': {
      color: theme.palette.error.main,
      animation: '$blink 1s infinite',
    },
  },
  '@keyframes blink': {
    '0%': { opacity: 1 },
    '50%': { opacity: 0.5 },
    '100%': { opacity: 1 },
  },
  progressContainer: {
    width: '100%',
    margin: '8px 0',
  },
  minimizedContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '60px',
    padding: '0 8px',
  },
  minimizedTimer: {
    fontSize: '1.2rem',
    fontWeight: 'bold',
    fontFamily: 'monospace',
  },
  statusChip: {
    marginTop: 8,
  }
}));

const TicketTimer = ({ ticket, visible = false }) => {
  const classes = useStyles();
  const {
    settings,
    activeTimers,
    getTimeRemaining,
    isNearTimeout,
    isTimeout
  } = useTimeMonitoring();

  const [minimized, setMinimized] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Atualizar o tempo atual a cada segundo
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Verificar se deve mostrar o timer
  const shouldShowTimer = () => {
    if (!visible || !ticket || !ticket.id) return false;
    if (!settings.timeMonitoringEnabled) return false;
    if (!activeTimers[ticket.id] && !ticket.responseTimeStart) return false;
    if (ticket.status !== 'open') return false;
    return true;
  };

  // Calcular informações do timer
  const getTimerInfo = () => {
    if (!shouldShowTimer()) return null;

    const ticketId = ticket.id;
    const queueSla = ticket.queue?.slaMinutes ? Number(ticket.queue.slaMinutes) : null;
    const limit = queueSla && queueSla > 0 ? queueSla : (settings.responseTimeLimit || 30);
    const warningLimit = ticket.queue?.slaWarningMinutes ? Number(ticket.queue.slaWarningMinutes) : (settings.timeMonitoringWarningMinutes || 5);

    let timeRemaining = getTimeRemaining(ticketId);
    let timer = activeTimers[ticketId];

    if ((timeRemaining === null || !timer) && ticket.responseTimeStart) {
      const elapsed = (Date.now() - new Date(ticket.responseTimeStart).getTime()) / (1000 * 60);
      timeRemaining = Math.round(limit - elapsed);
      timer = {
        startTime: new Date(ticket.responseTimeStart),
        limitMinutes: limit,
        warningMinutes: warningLimit
      };
    }

    if (timeRemaining === null || !timer) return null;

    const isNear = timeRemaining <= (timer.warningMinutes || warningLimit) && timeRemaining > 0;
    const isOvertime = timeRemaining <= 0 || ticket.responseTimeoutTriggered;

    return {
      timeRemaining,
      isNear,
      isOvertime,
      timer,
      ticketId
    };
  };

  // Formatar tempo para exibição
  const formatTime = (minutes) => {
    if (minutes < 0) {
      const absMinutes = Math.abs(minutes);
      const hours = Math.floor(absMinutes / 60);
      const mins = absMinutes % 60;
      return hours > 0 ? `-${hours}:${mins.toString().padStart(2, '0')}` : `-${mins}`;
    }

    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}:${mins.toString().padStart(2, '0')}` : `${mins}`;
  };

  // Calcular progresso
  const getProgress = (remaining, limit) => {
    if (remaining < 0) return 100;
    return ((limit - remaining) / limit) * 100;
  };

  // Obter cor do progresso
  const getProgressColor = (isNear, isOvertime) => {
    if (isOvertime) return 'error';
    if (isNear) return 'warning';
    return 'primary';
  };

  // Obter status text
  const getStatusText = (isNear, isOvertime, remaining) => {
    if (isOvertime) return i18n.t('timeMonitoring.timer.status.timeout');
    if (isNear) return `${i18n.t('timeMonitoring.timer.warning')}: ${remaining}min ${i18n.t('timeMonitoring.timer.timeRemaining')}`;
    return `${remaining}min ${i18n.t('timeMonitoring.timer.timeRemaining')}`;
  };

  const timerInfo = getTimerInfo();

  if (!timerInfo) return null;

  const { timeRemaining, isNear, isOvertime, timer } = timerInfo;
  const formattedTime = formatTime(timeRemaining);
  const progress = getProgress(timeRemaining, timer.limitMinutes);
  const progressColor = getProgressColor(isNear, isOvertime);
  const statusText = getStatusText(isNear, isOvertime, Math.abs(timeRemaining));

  const cardClassName = `${classes.timerCard} ${isOvertime ? 'timeout' : isNear ? 'warning' : ''} ${minimized ? 'minimized' : ''}`;

  return (
    <Card className={cardClassName}>
      {!minimized && (
        <Box className={classes.timerHeader}>
          <Box display="flex" alignItems="center">
            {isOvertime ? (
              <ErrorIcon color="error" />
            ) : isNear ? (
              <WarningIcon color="warning" />
            ) : (
              <TimerIcon color="primary" />
            )}
            <Typography variant="caption" style={{ marginLeft: 8 }}>
              Ticket #{ticket.id}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => setMinimized(true)}
            aria-label={i18n.t('timeMonitoring.timer.minimizeTimer')}
          >
            <MinimizeIcon />
          </IconButton>
        </Box>
      )}

      {minimized ? (
        <Box className={classes.minimizedContent} onClick={() => setMinimized(false)}>
          <Typography 
            className={`${classes.minimizedTimer} ${isOvertime ? 'timeout' : isNear ? 'warning' : ''}`}
          >
            {formattedTime}m
          </Typography>
          {isOvertime && <ErrorIcon color="error" style={{ marginLeft: 4 }} />}
          {isNear && !isOvertime && <WarningIcon color="warning" style={{ marginLeft: 4 }} />}
        </Box>
      ) : (
        <CardContent className={classes.timerContent}>
          <Typography variant="body2" color="textSecondary">
            {i18n.t('timeMonitoring.timer.title')}
          </Typography>
          
          <Typography 
            className={`${classes.timerDisplay} ${isOvertime ? 'timeout' : isNear ? 'warning' : ''}`}
          >
            {formattedTime}
          </Typography>

          <Box className={classes.progressContainer}>
            <LinearProgress
              variant="determinate"
              value={Math.min(progress, 100)}
              color={progressColor}
              style={{ height: 6, borderRadius: 3 }}
            />
          </Box>

          <StandardBadge
            label={statusText}
            color={isOvertime ? 'error' : isNear ? 'warning' : 'primary'}
            variant={isOvertime || isNear ? 'filled' : 'outlined'}
            className={classes.statusChip}
          />

          <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 8 }}>
            {i18n.t('timeMonitoring.timer.timeLimit')}: {timer.limitMinutes} {i18n.t('timeMonitoring.timer.minutes')}
          </Typography>
        </CardContent>
      )}
    </Card>
  );
};

export default TicketTimer;